import { CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import {
  useGenerateCampaignMutation,
  useGetGenerateCampaignResultQuery,
} from "../../../../api";
import { ProductAnalysisResponse } from "../../../../types/apiTypes";
import { IconWrapper } from "../../IconWrapper";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { AnalysisStatus, SSEEvents } from "../../../../types/companyTypes";
import { setStatus } from "features/companySlice";

type Props = {
  files: File[];
  productUrl: string;
  setResultHandler: (result: ProductAnalysisResponse) => void;
  stopAnalyzeHandler: () => void;
};

export const AnalysePDFBlock = ({
  files,
  setResultHandler,
  productUrl,
  stopAnalyzeHandler,
}: Props) => {
  const dispatch = useAppDispatch();
  const { generation_status } = useAppSelector((state) => state.company.status);

  const [progress, setProgress] = useState<number>(0);
  const [getResult, setGetResult] = useState<boolean>(false);
  const [generateCampaign, { data: generateData }] =
    useGenerateCampaignMutation();

  const { data: generateCampaignResult } = useGetGenerateCampaignResultQuery(
    generateData?.task_id || "",
    {
      skip: !getResult || !generateData?.task_id,
      refetchOnMountOrArgChange: true,
    },
  );

  const handleStatusUpdate = (
    status: typeof generation_status,
    source: string,
  ) => {
    if (!status) return;

    setProgress(status.progress);

    if (status.progress === 100) {
      setGetResult(true);
    }

    if (status.status === AnalysisStatus.failed) {
      stopAnalyzeHandler();
      dispatch(setStatus({ [SSEEvents.generation_status]: undefined }));
    }
  };

  useEffect(() => {
    generateCampaign({ url: productUrl || undefined, pdf_files: files })
      .unwrap()
      .then((data) => console.log(`[Campaign] Task created: ${data.task_id}`));
  }, []);

  useEffect(() => {
    if (!generateData?.task_id) return;

    if (!generation_status) {
      return;
    }

    if (generation_status.task_id !== generateData.task_id) {
      return;
    }

    handleStatusUpdate(generation_status, "catch-up");
  }, [generateData?.task_id]);

  useEffect(() => {
    if (!generation_status) return;

    if (!generateData?.task_id) {
      return;
    }

    if (generation_status.task_id !== generateData.task_id) {
      return;
    }

    handleStatusUpdate(generation_status, "sse");
  }, [generation_status]);

  useEffect(() => {
    if (!generateCampaignResult) return;

    setResultHandler(generateCampaignResult);
    setGetResult(false);
  }, [generateCampaignResult]);

  return (
    <Stack
      sx={{
        padding: "36px",
        borderRadius: "8px",
        textAlign: "center",
        backgroundColor: "#FCFCFC",
        transition: "background-color 0.2s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
      }}
    >
      <IconWrapper width="34px" height="34px">
        <CircularProgress size={24} sx={{ color: "#4C545B" }} />
      </IconWrapper>
      <Typography
        variant="button"
        color="#4C545B"
        sx={{
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {`Analyzing: ${progress}%`}
      </Typography>
    </Stack>
  );
};
